import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Select, DatePicker, Table, Tag, Modal, message, Space, Typography, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, BellOutlined } from '@ant-design/icons';
import axios from 'axios';
import Sidebar from '../components/sidebar';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const CrearNotificaciones = () => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    titulo: '',
    mensaje: '',
    tipo: 'info',
    enlace_campana: '',
    admin_id: 1, // ID del administrador actual
    admin_nombre: 'Administrador', // Nombre del administrador actual
    fecha_expiracion: null,
    prioridad: 'media',
    mostrar_en_landing: 1
  });

  const API_BASE = 'https://api.mrcirp.com/public';

  // Función para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Cargar notificaciones
  const cargarNotificaciones = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/notificaciones`);
      setNotificaciones(response.data);
    } catch (error) {
      message.error('Error al cargar las notificaciones');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarNotificaciones();
  }, []);

  // Manejar cambios en el formulario
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Crear o actualizar notificación
  const handleSubmit = async () => {
    try {
      const dataToSend = {
        ...formData,
        fecha_expiracion: formData.fecha_expiracion ? formData.fecha_expiracion.format('YYYY-MM-DD HH:mm:ss') : null
      };

      if (editingNotification) {
        // Actualizar
        await axios.put(`${API_BASE}/notificaciones/${editingNotification.id}`, dataToSend);
        message.success('Notificación actualizada exitosamente');
      } else {
        // Crear
        await axios.post(`${API_BASE}/notificaciones`, dataToSend);
        message.success('Notificación creada exitosamente');
      }

      setModalVisible(false);
      setEditingNotification(null);
      setFormData({
        titulo: '',
        mensaje: '',
        tipo: 'info',
        enlace_campana: '',
        admin_id: 1,
        admin_nombre: 'Administrador',
        fecha_expiracion: null,
        prioridad: 'media',
        mostrar_en_landing: 1
      });
      cargarNotificaciones();
    } catch (error) {
      message.error('Error al guardar la notificación');
      console.error('Error:', error);
    }
  };

  // Editar notificación
  const handleEdit = (record) => {
    setEditingNotification(record);
    setFormData({
      titulo: record.titulo,
      mensaje: record.mensaje,
      tipo: record.tipo,
      enlace_campana: record.enlace_campana || '',
      admin_id: record.admin_id,
      admin_nombre: record.admin_nombre,
      fecha_expiracion: record.fecha_expiracion ? new Date(record.fecha_expiracion) : null,
      prioridad: record.prioridad,
      mostrar_en_landing: record.mostrar_en_landing
    });
    setModalVisible(true);
  };

  // Eliminar notificación
  const handleDelete = async (id) => {
    Modal.confirm({
      title: '¿Estás seguro?',
      content: 'Esta acción no se puede deshacer',
      okText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await axios.delete(`${API_BASE}/notificaciones/${id}`);
          message.success('Notificación eliminada exitosamente');
          cargarNotificaciones();
        } catch (error) {
          message.error('Error al eliminar la notificación');
          console.error('Error:', error);
        }
      }
    });
  };

  // Marcar como leída
  const handleMarkAsRead = async (id) => {
    try {
      await axios.put(`${API_BASE}/notificaciones/${id}/leer`);
      message.success('Notificación marcada como leída');
      cargarNotificaciones();
    } catch (error) {
      message.error('Error al marcar como leída');
      console.error('Error:', error);
    }
  };

  // Configurar columnas de la tabla
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Título',
      dataIndex: 'titulo',
      key: 'titulo',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Prioridad',
      dataIndex: 'prioridad',
      key: 'prioridad',
      width: 80,
      render: (prioridad) => {
        const colors = {
          baja: 'default',
          media: 'blue',
          alta: 'red'
        };
        return <Tag color={colors[prioridad]}>{prioridad.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Tag color={record.activa ? 'green' : 'red'} style={{ margin: 0 }}>
            {record.activa ? 'ACTIVA' : 'INACTIVA'}
          </Tag>
          {!record.leida && (
            <Tag color="orange" style={{ margin: 0 }}>
              NO LEÍDA
            </Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Fecha Creación',
      dataIndex: 'fecha_creacion',
      key: 'fecha_creacion',
      width: 140,
      render: (fecha) => formatDate(fecha),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Editar"
          />
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleMarkAsRead(record.id)}
            disabled={record.leida}
            title="Marcar como leída"
          />
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            title="Eliminar"
          />
        </Space>
      )
    }
  ];

  return (
    <div>
    <Sidebar>
    <div style={{ display: 'flex', minHeight: '100vh' }}>
     
      
      <div style={{ flex: 1, padding: '24px', backgroundColor: '#f5f5f5' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
              <BellOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
              Gestión de Notificaciones
            </Title>
            <Text type="secondary">
              Administra las notificaciones del sistema
            </Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => {
                setEditingNotification(null);
                setFormData({
                  titulo: '',
                  mensaje: '',
                  tipo: 'info',
                  enlace_campana: '',
                  admin_id: 1,
                  admin_nombre: 'Administrador',
                  fecha_expiracion: null,
                  prioridad: 'media',
                  mostrar_en_landing: 1
                });
                setModalVisible(true);
              }}
            >
              Nueva Notificación
            </Button>
          </Col>
        </Row>

        <Card>
          <Table
            columns={columns}
            dataSource={notificaciones}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} notificaciones`
            }}
            scroll={{ x: 800 }}
          />
        </Card>

        {/* Modal para crear/editar notificación */}
        <Modal
          title={editingNotification ? 'Editar Notificación' : 'Nueva Notificación'}
          open={modalVisible}
          onOk={handleSubmit}
          onCancel={() => {
            setModalVisible(false);
            setEditingNotification(null);
          }}
          width={600}
          okText="Guardar"
          cancelText="Cancelar"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <label>Título *</label>
              <Input
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                placeholder="Título de la notificación"
              />
            </div>

            <div>
              <label>Mensaje *</label>
              <TextArea
                value={formData.mensaje}
                onChange={(e) => handleInputChange('mensaje', e.target.value)}
                placeholder="Mensaje de la notificación"
                rows={4}
              />
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <label>Tipo</label>
                <Select
                  value={formData.tipo}
                  onChange={(value) => handleInputChange('tipo', value)}
                  style={{ width: '100%' }}
                >
                  <Option value="info">Info</Option>
                  <Option value="success">Éxito</Option>
                  <Option value="warning">Advertencia</Option>
                  <Option value="error">Error</Option>
                </Select>
              </Col>
              <Col span={12}>
                <label>Prioridad</label>
                <Select
                  value={formData.prioridad}
                  onChange={(value) => handleInputChange('prioridad', value)}
                  style={{ width: '100%' }}
                >
                  <Option value="baja">Baja</Option>
                  <Option value="media">Media</Option>
                  <Option value="alta">Alta</Option>
                </Select>
              </Col>
            </Row>

            <div>
              <label>Enlace de Campaña (opcional)</label>
              <Input
                value={formData.enlace_campana}
                onChange={(e) => handleInputChange('enlace_campana', e.target.value)}
                placeholder="Enlace específico de campaña"
              />
            </div>

            <div>
              <label>Fecha de Expiración (opcional)</label>
              <DatePicker
                value={formData.fecha_expiracion}
                onChange={(date) => handleInputChange('fecha_expiracion', date)}
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                style={{ width: '100%' }}
                placeholder="Seleccionar fecha de expiración"
              />
            </div>

            <div>
              <label>
                <input
                  type="checkbox"
                  checked={formData.mostrar_en_landing}
                  onChange={(e) => handleInputChange('mostrar_en_landing', e.target.checked ? 1 : 0)}
                />
                {' '}Mostrar en páginas de landing
              </label>
            </div>
          </Space>
        </Modal>
      </div>
    </div>
    </Sidebar>
     </div>
  );
};

export default CrearNotificaciones;